/* eslint-disable max-lines */
/* eslint-disable prefer-template */
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useForm, Controller, SubmitHandler } from "react-hook-form";
import * as Sentry from "@sentry/react";
import { API, graphqlOperation } from "aws-amplify";
import queryString from "query-string";
import FirstQ from "../../components/FirstQ";
import Logo from "../../assets/E.ON_Next_logo.svg";
import {
  CANT_DECIDE,
  choicesSecondQuestion,
  choicesThirdQuestion,
  NAILED_IT,
  NOT_REALLY,
  CALL,
  SOCIAL,
  EMAIL,
} from "../../lib/choices";
import SecondQ from "../../components/SecondQ";
import ThirdQ from "../../components/ThirdQ";
import { FirstAnswer, ThirdAnswer } from "../../lib/types";
import CREATE_TYPE_FORM_RESPONSES from "../../lib/queries";
import {
  ESCALATE_QUESTION,
  FIRST_QUESTION,
  ISSUE_RESOLVED,
} from "../../lib/mapping";

interface CHIForm {
  firstAnswer: FirstAnswer;
  secondAnswer: string;
  thirdAnswer: ThirdAnswer;
}

/** Home page */
const Home = (): JSX.Element => {
  const location = useLocation();
  const [deviceWidth, setDeviceWidth] = useState(window.innerWidth);
  const [landedAt, setLandedAt] = useState("");
  const [page, setPage] = useState(0);
  const { id } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { watch, handleSubmit, control } = useForm<CHIForm>();

  useEffect(() => {
    const landed = new Date();
    setLandedAt(landed.toISOString());
    /** get window width to find  */
    const handleResize = () => {
      setDeviceWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
  }, [deviceWidth]);

  useEffect(() => {
    /** Check URL Request */
    async function CheckURL() {
      // const params = { url: window.location.pathname };
      const url = process.env.REACT_APP_VERIFY_SURVEY_LAMBDA
        ? process.env.REACT_APP_VERIFY_SURVEY_LAMBDA +
          "?" +
          window.location.pathname.split("/")[1]
        : "";
      try {
        const result = await fetch(url);

        if (result.status !== 200) {
          navigate("/invalid");
        }
      } catch (error) {
        navigate("/invalid");
      }
    }
    CheckURL();
  }, [location.pathname, navigate]);

  /** Change question displayed */
  const changeView = (num: number) => {
    if (page === 0 && num < 0) return;
    if (page === 2 && num > 2) return;
    setPage(num);
  };

  /** submit */
  const onSubmit: SubmitHandler<CHIForm> = async (formAnswers) => {
    const { firstAnswer, secondAnswer } = formAnswers;
    let device = "Computer";
    if (deviceWidth < 400) device = "Phone";
    let positiveAnswer = null;
    let neutralAnswer = null;
    let negativeAnswer = null;
    let contactChannel = CALL;
    let chiRating = "1";
    let chiScore = "1";
    const formId = "2021/06/17 - 1";
    if (firstAnswer === NAILED_IT) {
      positiveAnswer = secondAnswer;
    }
    if (firstAnswer === CANT_DECIDE) {
      neutralAnswer = secondAnswer;
      chiRating = "0";
      chiScore = "0.5";
    }
    if (firstAnswer === NOT_REALLY) {
      negativeAnswer = secondAnswer;
      chiRating = "-1";
      chiScore = "0";
    }
    if (!!queryString.parse(search).isWebChat === true) {
      contactChannel = SOCIAL;
    } else if (!!queryString.parse(search).isEmail === true) {
      contactChannel = EMAIL;
    }
    const submittedAt = new Date();
    try {
      await API.graphql(
        graphqlOperation(CREATE_TYPE_FORM_RESPONSES, {
          landed_at: landedAt,
          submitted_at: submittedAt.toISOString(),
          form_id: formId,
          form_title: `E.ON Next Ops Customer Happiness Index - ${device}`,
          chi_rating_question: FIRST_QUESTION,
          chi_rating_answer: firstAnswer,
          chi_rating: chiRating,
          chi_score: chiScore,
          issue_resolved_question: ISSUE_RESOLVED,
          issue_resolved_answer: formAnswers.thirdAnswer,
          escalate_question: ESCALATE_QUESTION,
          escalate_answer: null,
          negative_feedback_question: choicesSecondQuestion[NOT_REALLY],
          negative_feedback_answer: negativeAnswer,
          neutral_feedback_question: choicesSecondQuestion[CANT_DECIDE],
          neutral_feedback_answer: neutralAnswer,
          positive_feedback_question: choicesSecondQuestion[NAILED_IT],
          positive_feedback_answer: positiveAnswer,
          call_uuid: id,
          is_web_chat: null,
          contact_channel: contactChannel,
          response_date: submittedAt.toISOString().split("T")[0],
        })
      );
      let pathname = window.location.pathname.endsWith("/")
        ? window.location.pathname.slice(0, -1)
        : window.location.pathname;
      pathname = pathname.replace("/", "?");

      const url = process.env.REACT_APP_DELETE_SURVEY_LAMBDA
        ? process.env.REACT_APP_DELETE_SURVEY_LAMBDA + pathname
        : "";
      fetch(url);

      navigate("/confirmation");
    } catch (e) {
      Sentry.captureException(e);
      navigate("/failed");
    }
  };

  return (
    <main className="main">
      <div className="logo-container">
        <img className="main-logo" alt="E.ON Next logo" src={Logo} />
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="questions">
        {!page && (
          <Controller
            name="firstAnswer"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FirstQ
                value={value}
                changeView={() => changeView(1)}
                setValue={(answer) => {
                  if (!watch("firstAnswer")) changeView(1);
                  onChange(answer);
                }}
              />
            )}
          />
        )}
        {page === 1 && (
          <Controller
            name="secondAnswer"
            control={control}
            render={({ field: { onChange, value } }) => (
              <SecondQ
                text={choicesSecondQuestion[watch("firstAnswer")]}
                value={value}
                setValue={(answer) => {
                  changeView(2);
                  onChange(answer);
                }}
              />
            )}
          />
        )}
        {page === 2 && (
          <Controller
            name="thirdAnswer"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ThirdQ
                text={choicesThirdQuestion[watch("firstAnswer")]}
                value={value}
                setValue={(answer) => {
                  changeView(3);
                  onChange(answer);
                }}
              />
            )}
          />
        )}
      </form>
      <footer className="footer">
        <div className="progress-section" />
        {watch("firstAnswer") && (
          <div className="movement-buttons">
            <button
              type="button"
              className="orange-button"
              onClick={() => changeView(page - 1)}
            >
              ˄
            </button>
            <span className="border" />
            <button
              type="button"
              className="orange-button"
              onClick={() => changeView(page + 1)}
            >
              ˅
            </button>
          </div>
        )}
      </footer>
    </main>
  );
};

export default Home;
